import { useEffect, useState, useContext } from "react";
import PageWrapper from "../../Components/Layout/PageWrapper/PageWrapper";
import VerticalFlexContainer from "../../Components/Layout/VerticalFlexContainer/VerticalFlexContainer";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./Welcome.module.css";
import NavigationBar from "../../Components/HighLevelComponents/NavigationBar/NavigationBar";

import config from "../../config";
import { useNavigate } from "react-router-dom";
import { useAuthenticatedFetch } from "../../Hooks/useFetch";
import { GoogleAnalyticsContext } from "../../Hooks/GoogleAnalyticsContext";
import toast from "react-hot-toast";
import { YippityUserContext } from "../../Hooks/YippityUserContext";

export default function Welcome() {
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  // Form state
  const [username, setUsername] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [role, setRole] = useState("");
  const [subjects, setSubjects] = useState<string[]>([]);
  const [useCases, setUseCases] = useState<string[]>([]);
  const [otherSubject, setOtherSubject] = useState("");
  const [otherUseCase, setOtherUseCase] = useState("");

  const navigate = useNavigate();
  const { logEvent } = useContext(GoogleAnalyticsContext);
  const { refetch } = useContext(YippityUserContext);
  const {
    res: userResponse,
    callAPI: createUser,
    isLoading,
  } = useAuthenticatedFetch({
    url: config.API_URI + "/user",
    method: "POST",
  });

  // Navigation functions
  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      logEvent("welcome_wizard_next_step", {
        step_from: currentStep,
        step_to: currentStep + 1,
        step_name: getStepName(currentStep + 1),
      });
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      logEvent("welcome_wizard_prev_step", {
        step_from: currentStep,
        step_to: currentStep - 1,
        step_name: getStepName(currentStep - 1),
      });
    }
  };

  const getStepName = (step: number) => {
    switch (step) {
      case 1:
        return "basic_info";
      case 2:
        return "role_selection";
      case 3:
        return "subject_interests";
      case 4:
        return "use_cases";
      default:
        return "unknown";
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return username.length >= 4 && startDate;
      case 2:
        return role !== "";
      case 3:
        // Must select at least one subject, and if "Other" is selected, must provide text
        const hasSubjects = subjects.length > 0;
        const otherSelected = subjects.includes("Other");
        const otherValid =
          !otherSelected || (otherSelected && otherSubject.trim().length > 0);
        return hasSubjects && otherValid;
      case 4:
        // Must select at least one use case, and if "Other" is selected, must provide text
        const hasUseCases = useCases.length > 0;
        const otherUseCaseSelected = useCases.includes("Other");
        const otherUseCaseValid =
          !otherUseCaseSelected ||
          (otherUseCaseSelected && otherUseCase.trim().length > 0);
        return hasUseCases && otherUseCaseValid;
      default:
        return false;
    }
  };

  // Survey helper functions
  const handleSubjectChange = (subject: string, checked: boolean) => {
    if (checked) {
      setSubjects([...subjects, subject]);
    } else {
      setSubjects(subjects.filter((s) => s !== subject));
      // Clear the "Other" input if "Other" is deselected
      if (subject === "Other") {
        setOtherSubject("");
      }
    }
  };

  const handleUseCaseChange = (useCase: string, checked: boolean) => {
    if (checked) {
      setUseCases([...useCases, useCase]);
    } else {
      setUseCases(useCases.filter((uc) => uc !== useCase));
      // Clear the "Other" input if "Other" is deselected
      if (useCase === "Other") {
        setOtherUseCase("");
      }
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isLoading) return;

    // Prepare final subjects and use cases with custom values
    const finalSubjects = subjects.map((subject) =>
      subject === "Other" && otherSubject.trim()
        ? `Other: ${otherSubject.trim()}`
        : subject
    );
    const finalUseCases = useCases.map((useCase) =>
      useCase === "Other" && otherUseCase.trim()
        ? `Other: ${otherUseCase.trim()}`
        : useCase
    );

    // Log comprehensive user survey completion
    logEvent("user_survey_complete", {
      user_role: role,
      subjects_selected: finalSubjects,
      subjects_count: finalSubjects.length,
      use_cases_selected: finalUseCases,
      use_cases_count: finalUseCases.length,
      has_custom_subject:
        subjects.includes("Other") && otherSubject.trim().length > 0,
      has_custom_use_case:
        useCases.includes("Other") && otherUseCase.trim().length > 0,
      custom_subject: subjects.includes("Other") ? otherSubject.trim() : null,
      custom_use_case: useCases.includes("Other") ? otherUseCase.trim() : null,
      profile_summary: `${role}|${finalSubjects.join(",")}|${finalUseCases.join(
        ","
      )}`,
    });

    await createUser(
      JSON.stringify({
        username,
        month: (startDate?.getMonth() ?? 0) + 1,
        day: startDate?.getDate() ?? 1,
        year: startDate?.getFullYear() ?? new Date().getFullYear(),
      })
    );

    refetch();
  };

  useEffect(() => {
    if (userResponse.statusCode === 200) {
      navigate("/tools/quiz");
    }

    if (userResponse.statusCode === 409) {
      toast.error("Username already in use");
    }

    if (userResponse.statusCode === 400) {
      toast.error(
        "Username can only contain letters, numbers and underscores and must be at least 6 characters long"
      );
    }
  }, [userResponse]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className={styles.stepContent}>
            <div className={styles.stepHeader}>
              <h1 className={styles.stepTitle}>Welcome to Yippity! 👋</h1>
              <p className={styles.stepSubtitle}>
                Let's start by getting to know you better
              </p>
            </div>
            <div className={styles.stepBody}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  What should we call you?
                </label>
                <input
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={styles.formInput}
                />
                {username.length > 0 && username.length < 4 && (
                  <p style={{ color: "#ef4444", fontSize: "12px" }}>
                    Username must be at least 4 characters long
                  </p>
                )}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  What's your date of birth?
                </label>
                <div className={styles.datePickerWrapper}>
                  <DatePicker
                    selected={startDate}
                    onChange={(date: Date | null) => setStartDate(date)}
                    className={styles.formInput}
                    placeholderText="Select your date of birth"
                    dateFormat="MM/dd/yyyy"
                    showYearDropdown
                    dropdownMode="select"
                  />
                </div>
              </div>
            </div>
            <div className={styles.navigationContainer}>
              <div></div>
              <button
                onClick={nextStep}
                disabled={!canProceed()}
                className={`${styles.navButton} ${styles.navButtonPrimary}`}
              >
                Next Step →
              </button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className={styles.stepContent}>
            <div className={styles.stepHeader}>
              <h1 className={styles.stepTitle}>What's your role? 👤</h1>
              <p className={styles.stepSubtitle}>
                This helps us customize your experience
              </p>
            </div>
            <div className={styles.stepBody}>
              <div className={styles.roleGrid}>
                {[
                  { value: "teacher", icon: "👩‍🏫", title: "Teacher" },
                  { value: "student", icon: "🎓", title: "Student" },
                  { value: "tutor", icon: "👨‍🎓", title: "Tutor" },
                  { value: "other", icon: "👤", title: "Other" },
                ].map((roleOption) => (
                  <div
                    key={roleOption.value}
                    onClick={() => setRole(roleOption.value)}
                    className={`${styles.roleCard} ${
                      role === roleOption.value ? styles.selected : ""
                    }`}
                  >
                    <div className={styles.roleIcon}>{roleOption.icon}</div>
                    <div className={styles.roleTitle}>{roleOption.title}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.navigationContainer}>
              <button
                onClick={prevStep}
                className={`${styles.navButton} ${styles.navButtonSecondary}`}
              >
                ← Back
              </button>
              <button
                onClick={nextStep}
                disabled={!canProceed()}
                className={`${styles.navButton} ${styles.navButtonPrimary}`}
              >
                Next Step →
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className={styles.stepContent}>
            <div className={styles.stepHeader}>
              <h1 className={styles.stepTitle}>
                What subjects interest you? 📚
              </h1>
              <p className={styles.stepSubtitle}>
                Select all that apply - this helps us personalize your
                experience
              </p>
            </div>
            <div className={styles.stepBody}>
              <div
                className={`${styles.selectionGrid} ${styles.selectionGridTwoColumns}`}
              >
                {[
                  { value: "Math", icon: "🔢" },
                  { value: "Science", icon: "🔬" },
                  { value: "English", icon: "📝" },
                  { value: "History", icon: "📜" },
                  { value: "Languages", icon: "🌍" },
                  { value: "Other", icon: "📖" },
                ].map((subject) => (
                  <div
                    key={subject.value}
                    onClick={() =>
                      handleSubjectChange(
                        subject.value,
                        !subjects.includes(subject.value)
                      )
                    }
                    className={`${styles.selectionCard} ${
                      subjects.includes(subject.value) ? styles.selected : ""
                    }`}
                  >
                    <div
                      className={`${styles.selectionCheckbox} ${
                        subjects.includes(subject.value) ? styles.checked : ""
                      }`}
                    >
                      {subjects.includes(subject.value) && "✓"}
                    </div>
                    <div className={styles.selectionText}>
                      {subject.icon} {subject.value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Other Subject Input */}
              {subjects.includes("Other") && (
                <div>
                  <label className={styles.otherInputLabel}>
                    Please specify your other subject interest:
                  </label>
                  <input
                    type="text"
                    value={otherSubject}
                    onChange={(e) => setOtherSubject(e.target.value)}
                    placeholder="e.g., Art, Music, Computer Science..."
                    className={styles.otherInput}
                  />
                </div>
              )}
            </div>
            <div className={styles.navigationContainer}>
              <button
                onClick={prevStep}
                className={`${styles.navButton} ${styles.navButtonSecondary}`}
              >
                ← Back
              </button>
              <button
                onClick={nextStep}
                disabled={!canProceed()}
                className={`${styles.navButton} ${styles.navButtonPrimary}`}
              >
                Next Step →
              </button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className={styles.stepContent}>
            <div className={styles.stepHeader}>
              <h1 className={styles.stepTitle}>How will you use Yippity? 🚀</h1>
              <p className={styles.stepSubtitle}>
                Select all that apply - this helps us recommend the best
                features for you
              </p>
            </div>
            <div className={styles.stepBody}>
              <div
                className={`${styles.selectionGrid} ${styles.selectionGridOneColumn}`}
              >
                {[
                  { value: "Creating quizzes for students", icon: "📝" },
                  { value: "Studying for exams", icon: "📖" },
                  { value: "Creating flashcards", icon: "🗂️" },
                  { value: "Generating practice questions", icon: "❓" },
                  { value: "Assessment and grading", icon: "📊" },
                  { value: "Personal learning", icon: "🎯" },
                  { value: "Other", icon: "💡" },
                ].map((useCase) => (
                  <div
                    key={useCase.value}
                    onClick={() =>
                      handleUseCaseChange(
                        useCase.value,
                        !useCases.includes(useCase.value)
                      )
                    }
                    className={`${styles.selectionCard} ${
                      useCases.includes(useCase.value) ? styles.selected : ""
                    }`}
                  >
                    <div
                      className={`${styles.selectionCheckbox} ${
                        useCases.includes(useCase.value) ? styles.checked : ""
                      }`}
                    >
                      {useCases.includes(useCase.value) && "✓"}
                    </div>
                    <div className={styles.selectionText}>
                      {useCase.icon} {useCase.value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Other Use Case Input */}
              {useCases.includes("Other") && (
                <div>
                  <label className={styles.otherInputLabel}>
                    Please describe how you plan to use Yippity:
                  </label>
                  <input
                    type="text"
                    value={otherUseCase}
                    onChange={(e) => setOtherUseCase(e.target.value)}
                    placeholder="e.g., Research assistance, Content creation..."
                    className={styles.otherInput}
                  />
                </div>
              )}
            </div>
            <div className={styles.navigationContainer}>
              <button
                onClick={prevStep}
                className={`${styles.navButton} ${styles.navButtonSecondary}`}
              >
                ← Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={!canProceed() || isLoading}
                className={`${styles.navButton} ${styles.navButtonPrimary}`}
              >
                {isLoading ? "Completing Setup..." : "Complete Setup 🎉"}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <PageWrapper>
      <NavigationBar />
      <VerticalFlexContainer
        justifyContent="center"
        alignItems="center"
        minHeight="90vh"
        padding="20px"
      >
        <div className={styles.wizardContainer}>
          {/* Progress Bar */}
          <div className={styles.progressContainer}>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            </div>
            <div className={styles.stepIndicator}>
              <div className={styles.stepNumber}>
                <div
                  className={`${styles.stepCircle} ${
                    currentStep >= 1 ? styles.active : ""
                  } ${currentStep > 1 ? styles.completed : ""}`}
                >
                  {currentStep > 1 ? "✓" : "👤"}
                </div>
                <div className={styles.stepLabel}>Info</div>
              </div>
              <div className={styles.stepNumber}>
                <div
                  className={`${styles.stepCircle} ${
                    currentStep >= 2 ? styles.active : ""
                  } ${currentStep > 2 ? styles.completed : ""}`}
                >
                  {currentStep > 2 ? "✓" : "👔"}
                </div>
                <div className={styles.stepLabel}>Role</div>
              </div>
              <div className={styles.stepNumber}>
                <div
                  className={`${styles.stepCircle} ${
                    currentStep >= 3 ? styles.active : ""
                  } ${currentStep > 3 ? styles.completed : ""}`}
                >
                  {currentStep > 3 ? "✓" : "📚"}
                </div>
                <div className={styles.stepLabel}>Subjects</div>
              </div>
              <div className={styles.stepNumber}>
                <div
                  className={`${styles.stepCircle} ${
                    currentStep >= 4 ? styles.active : ""
                  } ${currentStep > 4 ? styles.completed : ""}`}
                >
                  {currentStep > 4 ? "✓" : "🚀"}
                </div>
                <div className={styles.stepLabel}>Goals</div>
              </div>
            </div>
          </div>

          {/* Step Content */}
          {renderStepContent()}
        </div>
      </VerticalFlexContainer>
    </PageWrapper>
  );
}
